"use client";

import { useEffect, useId, useRef, useState } from "react";
import type { KeyboardEvent as ReactKeyboardEvent } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import type { LucideIcon } from "lucide-react";
import {
  ArrowRight,
  BellOff,
  ChevronRight,
  Clock,
  KeyRound,
  ListChecks,
  Loader2,
  MoreHorizontal,
  PauseCircle,
  Pencil,
  Play,
  Power,
  RotateCw,
  Send,
  Trash2,
} from "lucide-react";
import {
  ackWebhookAlerts,
  deleteWebhookEndpoint,
  updateWebhookEndpoint,
  type WebhookEndpoint,
} from "../../../services/merchantApi";
import {
  ConfirmDialog,
  CopyButton,
  InlineNotice,
  StatTile,
  StatusPill,
  Switch,
  TimeText,
  focusRing,
  iconBtn,
  secondaryBtn,
} from "../ui";
import { formatAbsolute, formatCount, formatPercent, formatUtc, hostOf } from "./format";
import { HttpStatusChip } from "./HttpStatusChip";
import { fallbackHint, healthUi } from "./vocab";
import { notifyWebhooksChanged } from "./useWebhookSummary";
import EndpointFormModal, { apiErrorOf, isUrlErrorCode, useCountdown } from "./EndpointFormModal";
import RotateSecretModal from "./RotateSecretModal";
import SendTestModal from "./SendTestModal";

// ────────────────────────────────────────────────────────────────────────────
// EndpointCard — one webhook endpoint: health, URL, switch, 24 h numbers,
// and whatever needs the merchant's attention right now (failing, disabled
// by CashTap, failed deliveries to review, paused, secret overlap).
//
// Interaction rules (spec §5.10):
//   pause            optimistic flip (after a confirm when deliveries are
//                    waiting), rollback + toast on failure
//   resume/re-enable never optimistic — "Checking URL…" until the server
//                    has re-resolved the URL
//   mark as reviewed optimistic zero of failedSinceAck, rollback on failure
//   edit/rotate/delete/test  run in their own dialogs, patched from the
//                    server response
// One action at a time per card; every mutation ends with
// notifyWebhooksChanged() so the badge / banner / summary card refresh.
// ────────────────────────────────────────────────────────────────────────────

const WEBHOOKS_PATH = "/developers/webhooks";

/** Link to the endpoint's own page. */
export function endpointPath(endpointId: string): string {
  return `${WEBHOOKS_PATH}/endpoints/${encodeURIComponent(endpointId)}`;
}

/** Link to the Deliveries tab filtered to one endpoint (+ optional status bucket). */
export function endpointDeliveriesPath(
  endpointId: string,
  status?: "failed" | "skipped" | "in_progress" | "succeeded",
): string {
  const p = new URLSearchParams({ tab: "deliveries", endpoint: endpointId });
  if (status) p.set("status", status);
  return `${WEBHOOKS_PATH}?${p.toString()}`;
}

export interface EndpointCardProps {
  wallet: string | null;
  email: string | null;
  endpoint: WebhookEndpoint;
  alertRecipient: string | null;
  secretWritesBlocked: boolean;
  /** true on EndpointDetailPage: no self-link, full URL, no "Lost the secret?" hint. */
  expanded?: boolean;
  /** Replace this endpoint in the parent's state. */
  onChanged: (next: WebhookEndpoint) => void;
  onDeleted: (endpointId: string) => void;
  /** A rotation produced a new secret — the parent shows it once. */
  onSecretRevealed: (s: {
    endpointId: string;
    host: string;
    secret: string;
    previousSecretExpiresAt: string | null;
  }) => void;
  /** A test delivery was created (parent bumps the Deliveries tab's staleKey). */
  onTestSent?: () => void;
}

type BusyAction = "enable" | "pause" | "ack" | null;
type Dialog = "edit" | "rotate" | "delete" | "test" | "pause" | null;

// Keeps stats from the previous copy when a response carries none (PATCH
// and rotate responses never include `stats24h`).
function withStats(next: WebhookEndpoint, prev: WebhookEndpoint): WebhookEndpoint {
  return { ...next, stats24h: next.stats24h ?? prev.stats24h };
}

function validDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

// "09:14" today, "Sep 15, 09:14" this year, "Sep 15, 2025, 09:14" otherwise.
function shortWhen(d: Date, dateOnly: boolean): string {
  const now = new Date();
  if (!dateOnly && d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  const opts: Intl.DateTimeFormatOptions = dateOnly
    ? { month: "short", day: "numeric" }
    : { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" };
  if (d.getFullYear() !== now.getFullYear()) opts.year = "numeric";
  return d.toLocaleString([], opts);
}

/** Absolute local time as visible text; local + UTC in the tooltip. */
function When({ iso, dateOnly = false }: { iso: string | null | undefined; dateOnly?: boolean }) {
  const d = validDate(iso);
  if (!d) return <>—</>;
  return (
    <time dateTime={iso as string} title={`${formatAbsolute(iso)} · ${formatUtc(iso)}`} className="tabular-nums">
      {shortWhen(d, dateOnly)}
    </time>
  );
}

function eventsSummary(events: string[]): { text: string; title?: string } {
  if (!events.length || events.includes("*")) return { text: "All events" };
  const short = events.map((e) => e.replace(/^checkout\.session\./, ""));
  return {
    text: short.length === 1 ? `${short[0]} only` : short.join(" + "),
    title: events.join(", "),
  };
}

function plural(n: number, one: string, many: string): string {
  return `${formatCount(n)} ${n === 1 ? one : many}`;
}

// In-card notices: a little less padding on phones, where the card is
// only ~184 px wide (the ui.tsx notice box hard-codes p-4 / gap-3).
const NOTICE_TIGHT = "!p-3 !gap-2.5 sm:!p-4 sm:!gap-3";

const noticeLink = {
  amber: "inline-flex items-center gap-1.5 py-1 text-xs font-semibold text-amber-300 hover:text-white",
  red: "inline-flex items-center gap-1.5 py-1 text-xs font-semibold text-red-200 hover:text-white",
  gray: "inline-flex items-center gap-1.5 py-1 text-xs font-semibold text-gray-300 hover:text-white",
  green: "inline-flex items-center gap-1.5 py-1 text-xs font-semibold text-[#61CE70] hover:text-white",
};

const noticeButton = {
  red: "inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-red-500/15 hover:bg-red-500/25 border border-red-500/30 text-red-100 text-xs font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed",
  gray: "inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-gray-700 text-gray-100 text-xs font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed",
  textRed:
    "inline-flex items-center gap-1.5 py-1 text-xs text-red-200 hover:text-white disabled:opacity-60 disabled:cursor-not-allowed",
};

/**
 * One endpoint, with every state that needs the merchant's attention.
 */
export default function EndpointCard({
  wallet,
  email,
  endpoint: ep,
  alertRecipient,
  secretWritesBlocked,
  expanded = false,
  onChanged,
  onDeleted,
  onSecretRevealed,
  onTestSent,
}: EndpointCardProps) {
  const host = ep.urlHost || hostOf(ep.url);
  // The newest props for async handlers: a rollback must not undo changes
  // that landed while the request was in flight.
  const latest = useRef(ep);
  latest.current = ep;

  const [busy, setBusy] = useState<BusyAction>(null);
  const busyRef = useRef<BusyAction>(null);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [deleting, setDeleting] = useState(false);
  const deletingRef = useRef(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [enableError, setEnableError] = useState<{ message: string; urlProblem: boolean } | null>(null);
  const [resumedFrom, setResumedFrom] = useState<"paused" | "disabled" | null>(null);
  const cooldown = useCountdown();
  const deleteCooldown = useCountdown();

  const begin = (action: Exclude<BusyAction, null>): boolean => {
    if (busyRef.current || deletingRef.current) return false;
    busyRef.current = action;
    setBusy(action);
    return true;
  };
  const finish = () => {
    busyRef.current = null;
    setBusy(null);
    notifyWebhooksChanged();
  };

  // ── Pause (optimistic) ────────────────────────────────────────────────
  const waitingCount = ep.stats24h?.inProgress ?? 0;
  const mayHaveWaiting = waitingCount > 0 || (!ep.stats24h && ep.health === "failing");

  const pause = async () => {
    if (!begin("pause")) return;
    const prev = latest.current;
    setEnableError(null);
    setResumedFrom(null);
    onChanged({
      ...prev,
      enabled: false,
      health: "paused",
      disabledReason: "user",
      disabledAt: new Date().toISOString(),
    });
    try {
      const res = await updateWebhookEndpoint(wallet, email, prev.id, { enabled: false });
      onChanged(withStats(res.endpoint, latest.current));
      toast.success(
        res.waitingSkipped > 0
          ? `Endpoint paused. ${plural(res.waitingSkipped, "waiting delivery", "waiting deliveries")} will be skipped.`
          : "Endpoint paused.",
      );
    } catch (err) {
      const e = apiErrorOf(err);
      const cur = latest.current;
      onChanged({
        ...cur,
        enabled: prev.enabled,
        health: prev.health,
        disabledReason: prev.disabledReason,
        disabledAt: prev.disabledAt,
      });
      if (e.status === 429) cooldown.start(e.retryAfter);
      toast.error(e.message || "Couldn't pause the endpoint.");
    } finally {
      finish();
    }
  };

  // ── Resume / re-enable (never optimistic) ─────────────────────────────
  const enable = async () => {
    if (!begin("enable")) return;
    const from = latest.current.health === "disabled" ? "disabled" : "paused";
    setEnableError(null);
    try {
      const res = await updateWebhookEndpoint(wallet, email, latest.current.id, { enabled: true });
      onChanged(withStats(res.endpoint, latest.current));
      setResumedFrom(from);
      toast.success("Endpoint enabled.");
    } catch (err) {
      const e = apiErrorOf(err);
      if (e.status === 429) {
        cooldown.start(e.retryAfter);
      } else if (isUrlErrorCode(e.code)) {
        setEnableError({ message: e.message || "This URL no longer passes our checks.", urlProblem: true });
      } else if (e.code === "webhooks_unavailable") {
        setEnableError({ message: "Webhooks are temporarily paused on our side. Please try again in a few minutes.", urlProblem: false });
      } else {
        toast.error(e.message || "Couldn't enable the endpoint.");
      }
    } finally {
      finish();
    }
  };

  const onToggle = (next: boolean) => {
    if (busyRef.current || deletingRef.current || cooldown.active) return;
    if (next) {
      void enable();
    } else if (mayHaveWaiting) {
      setDialog("pause");
    } else {
      void pause();
    }
  };

  // ── Mark failed deliveries as reviewed (optimistic) ───────────────────
  const ack = async () => {
    if (!begin("ack")) return;
    const prev = latest.current;
    onChanged({ ...prev, failedSinceAck: 0, firstUnackedFailureAt: null, lastFailedDeliveryId: null });
    try {
      await ackWebhookAlerts(wallet, email, prev.id);
      toast.success("Marked as reviewed.");
    } catch (err) {
      const e = apiErrorOf(err);
      const cur = latest.current;
      onChanged({
        ...cur,
        failedSinceAck: prev.failedSinceAck,
        firstUnackedFailureAt: prev.firstUnackedFailureAt,
        lastFailedDeliveryId: prev.lastFailedDeliveryId,
      });
      if (e.status === 429) cooldown.start(e.retryAfter);
      toast.error(e.message || "Couldn't mark as reviewed.");
    } finally {
      finish();
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────
  const remove = async () => {
    if (deletingRef.current || busyRef.current || deleteCooldown.active) return;
    deletingRef.current = true;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteWebhookEndpoint(wallet, email, ep.id);
      toast.success("Endpoint deleted.");
      setDialog(null);
      onDeleted(ep.id);
    } catch (err) {
      const e = apiErrorOf(err);
      if (e.status === 429) deleteCooldown.start(e.retryAfter);
      setDeleteError(e.message || "Couldn't delete the endpoint. Please try again.");
    } finally {
      deletingRef.current = false;
      setDeleting(false);
      notifyWebhooksChanged();
    }
  };

  const openDialog = (d: Exclude<Dialog, null>) => {
    if (busyRef.current || deletingRef.current) return;
    if (d === "delete") setDeleteError(null);
    setDialog(d);
  };

  // ── Derived display state ─────────────────────────────────────────────
  const anyBusy = busy !== null || deleting;
  const health = ep.health;
  const failedCount = ep.failedSinceAck || 0;
  const stats = ep.stats24h;
  const statsTotal = stats ? stats.succeeded + stats.failed + stats.inProgress + stats.skipped : 0;
  const idle = health === "idle" && (!stats || statsTotal === 0);
  const prevExpiry = validDate(ep.previousSecretExpiresAt);
  const overlap = !!prevExpiry && prevExpiry.getTime() > Date.now();
  const events = eventsSummary(ep.events || []);
  const le = ep.lastError;

  const border =
    health === "disabled" || failedCount > 0
      ? "border-red-500/30 hover:border-red-500/40"
      : health === "failing"
        ? "border-amber-500/30 hover:border-amber-500/40"
        : "border-gray-800/60 hover:border-gray-700";

  const menuItems: MenuItem[] = [
    { key: "edit", label: "Edit endpoint", icon: Pencil, onSelect: () => openDialog("edit") },
    {
      key: "rotate",
      label: "Rotate secret",
      icon: RotateCw,
      onSelect: () => openDialog("rotate"),
      disabled: secretWritesBlocked,
      hint: "Paused on our side right now",
    },
    { key: "test", label: "Send test event", icon: Send, onSelect: () => openDialog("test") },
    { key: "delete", label: "Delete endpoint", icon: Trash2, onSelect: () => openDialog("delete"), danger: true },
  ];

  const streak = ep.consecutiveFailures || 0;
  let disabledTitle = "Disabled by CashTap";
  const disabledAt = validDate(ep.disabledAt);
  const failingSince = validDate(ep.failingSince);
  if (disabledAt) disabledTitle += ` on ${shortWhen(disabledAt, true)}`;
  if (streak > 0) {
    disabledTitle += ` after ${plural(streak, "failed attempt", "failed attempts")}`;
    if (failingSince && disabledAt && disabledAt > failingSince) {
      const days = Math.max(1, Math.round((disabledAt.getTime() - failingSince.getTime()) / 86_400_000));
      disabledTitle += ` over ${plural(days, "day", "days")}`;
    }
  }
  disabledTitle += ".";

  const enableButton = (tone: "red" | "gray", label: string, Icon: LucideIcon) => (
    <button
      type="button"
      onClick={() => onToggle(true)}
      disabled={anyBusy || cooldown.active}
      className={`${noticeButton[tone]} ${focusRing}`}
    >
      {busy === "enable" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />}
      {busy === "enable" ? "Checking URL…" : label}
    </button>
  );

  const errorLine = le ? (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
      <HttpStatusChip status={le.responseStatus} errorCode={le.code} />
      <span className="break-words min-w-0">{le.message}</span>
    </div>
  ) : null;
  const errorHint = le ? le.hint ?? fallbackHint(le.code, le.responseStatus) : null;

  return (
    <article
      aria-label={`Webhook endpoint ${host}`}
      className={`rounded-xl bg-black/20 border ${border} transition-colors p-3.5 sm:p-4`}
    >
      {/* ── Header ─────────────────────────────────────────────────────── */}
      {/* Only the host shares its line with the switch + menu; everything
          below runs full width (at 320 px the card is ~184 px wide). */}
      <div className="flex items-start gap-3">
        <div className={`min-w-0 flex-1 flex gap-2 pt-1 ${expanded ? "items-start" : "items-center"}`}>
          {/* sm+: [pill] host. Below sm the pill moves to its own line. */}
          <StatusPill ui={healthUi(ep)} className="hidden sm:inline-flex shrink-0" />
          {expanded ? (
            <p className="text-white text-sm font-semibold [overflow-wrap:anywhere] min-w-0">{host}</p>
          ) : (
            <Link
              to={endpointPath(ep.id)}
              className={`block min-w-0 truncate text-white text-sm font-semibold hover:text-[#61CE70] transition-colors rounded-sm ${focusRing}`}
            >
              {host}
            </Link>
          )}
        </div>

        <div className="flex items-center gap-1 shrink-0 -mr-1.5 -my-1">
          <Switch
            checked={ep.enabled}
            onChange={onToggle}
            label="Deliveries enabled"
            busy={busy === "enable" || busy === "pause"}
            disabled={anyBusy || cooldown.active}
          />
          <ActionMenu label={`Actions for ${host}`} items={menuItems} disabled={anyBusy} />
        </div>
      </div>

      <div className="sm:hidden mt-1.5">
        <StatusPill ui={healthUi(ep)} />
      </div>

      <div className="mt-1.5 flex items-center gap-0.5 min-w-0">
        <p
          className={`min-w-0 font-mono text-xs text-gray-400 ${expanded ? "break-all" : "truncate"}`}
          title={ep.url}
        >
          {ep.url}
        </p>
        <CopyButton text={ep.url} label="Copy endpoint URL" className="-my-2" />
      </div>

      {ep.description && <p className="mt-1 text-xs text-gray-300 break-words">{ep.description}</p>}

      <div className="mt-2 flex flex-wrap items-center gap-x-3.5 gap-y-1 text-[11px] text-gray-500">
        <span className="inline-flex items-center gap-1 min-w-0" title={events.title}>
          <ListChecks className="w-3 h-3 shrink-0 text-gray-600" aria-hidden="true" />
          <span className="sr-only">Events: </span>
          {events.text}
        </span>
        <span className="inline-flex items-center gap-1 whitespace-nowrap">
          <KeyRound className="w-3 h-3 shrink-0 text-gray-600" aria-hidden="true" />
          secret <span className="font-mono text-gray-400">••••{ep.secretLast4}</span>
        </span>
        <span className="inline-flex items-center gap-1 whitespace-nowrap">
          <Clock className="w-3 h-3 shrink-0 text-gray-600" aria-hidden="true" />
          created <TimeText iso={ep.createdAt} />
        </span>
        {!ep.emailAlerts && (
          <span className="inline-flex items-center gap-1 whitespace-nowrap">
            <BellOff className="w-3 h-3 shrink-0 text-gray-600" aria-hidden="true" /> email alerts off
          </span>
        )}
      </div>

      {!expanded && !secretWritesBlocked && (
        <button
          type="button"
          onClick={() => openDialog("rotate")}
          disabled={anyBusy}
          className={`mt-1 -ml-0.5 px-0.5 py-1 rounded-sm text-left text-[11px] text-gray-500 hover:text-white hover:underline underline-offset-2 disabled:opacity-60 ${focusRing}`}
        >
          Lost the secret? Rotate it.
        </button>
      )}

      {cooldown.active && (
        <p role="status" className="mt-2 inline-flex items-center gap-1.5 text-xs text-amber-300 tabular-nums">
          <Clock className="w-3.5 h-3.5" aria-hidden="true" />
          Too many changes in a short time. {cooldown.text}.
        </p>
      )}

      {/* ── Numbers ────────────────────────────────────────────────────── */}
      {stats && !idle && (
        <>
          <StatsBlock stats={stats} />
          {stats.skipped > 0 && (
            <p className="mt-2 text-[11px] text-gray-500">
              {plural(stats.skipped, "event was", "events were")} skipped in the last 24 hours.{" "}
              <Link to={endpointDeliveriesPath(ep.id, "skipped")} className="text-gray-300 hover:text-white underline-offset-2 hover:underline">
                View skipped
              </Link>
            </p>
          )}
        </>
      )}

      {/* ── What needs attention ───────────────────────────────────────── */}
      <div className="empty:hidden mt-4 space-y-3">
        {health === "disabled" && (
          <InlineNotice
            tone="error"
            className={NOTICE_TIGHT}
            title={disabledTitle}
            action={
              <>
                {enableButton("red", "Re-enable", Power)}
                <Link to={endpointDeliveriesPath(ep.id, "skipped")} className={noticeLink.red}>
                  Skipped events <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </>
            }
          >
            <p>
              New events are being skipped (kept 30 days). Fix your server, then re-enable — you can resend
              skipped events from the delivery history.
            </p>
            {errorLine && <div className="mt-2">{errorLine}</div>}
          </InlineNotice>
        )}

        {health === "failing" && (
          <InlineNotice
            tone="warn"
            className={NOTICE_TIGHT}
            title={
              <>
                {failingSince ? (
                  <>
                    Failing since <When iso={ep.failingSince} />
                  </>
                ) : (
                  "Failing"
                )}
                {streak > 0 && ` · ${plural(streak, "failed attempt", "failed attempts")} in a row`}
              </>
            }
            action={
              le?.deliveryId ? (
                <Link to={`${WEBHOOKS_PATH}/deliveries/${encodeURIComponent(le.deliveryId)}`} className={noticeLink.amber}>
                  View delivery <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              ) : undefined
            }
          >
            {errorLine ?? <p>Recent attempts to reach your server failed.</p>}
            {errorHint && <p className="mt-1.5 text-xs text-amber-200/60">{errorHint}</p>}
            <p className="mt-1.5 text-xs text-amber-200/60">
              We're retrying automatically — each event is tried up to 6 times over about 12 hours.
            </p>
          </InlineNotice>
        )}

        {failedCount > 0 && (
          <InlineNotice
            tone="error"
            className={NOTICE_TIGHT}
            title={`${plural(failedCount, "delivery", "deliveries")} failed permanently.`}
            action={
              <>
                <Link to={endpointDeliveriesPath(ep.id, "failed")} className={noticeLink.red}>
                  Review <ArrowRight className="w-3.5 h-3.5" />
                </Link>
                <button
                  type="button"
                  onClick={ack}
                  disabled={anyBusy || cooldown.active}
                  className={`${noticeButton.textRed} rounded-sm ${focusRing}`}
                >
                  {busy === "ack" && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  Mark as reviewed
                </button>
              </>
            }
          >
            All retries were used. Review them and resend once your endpoint is fixed.
          </InlineNotice>
        )}

        {health === "paused" && (
          <InlineNotice
            icon={PauseCircle}
            title={
              ep.disabledAt ? (
                <>
                  Paused since <When iso={ep.disabledAt} />
                </>
              ) : (
                "Paused"
              )
            }
            action={
              <>
                {enableButton("gray", "Resume deliveries", Play)}
                <Link to={endpointDeliveriesPath(ep.id, "skipped")} className={noticeLink.gray}>
                  Skipped events <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </>
            }
          >
            New events are skipped while the endpoint is paused and kept for 30 days. You can resend skipped
            events from the delivery history.
          </InlineNotice>
        )}

        {enableError && (
          <InlineNotice
            tone="error"
            role="alert"
            className={NOTICE_TIGHT}
            title="We couldn't enable this endpoint"
            action={
              <>
                {enableError.urlProblem && (
                  <button
                    type="button"
                    onClick={() => openDialog("edit")}
                    disabled={anyBusy}
                    className={`${noticeButton.textRed} font-semibold rounded-sm ${focusRing}`}
                  >
                    <Pencil className="w-3.5 h-3.5" /> Edit URL
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setEnableError(null)}
                  className={`${noticeButton.textRed} rounded-sm ${focusRing}`}
                >
                  Dismiss
                </button>
              </>
            }
          >
            {enableError.message}
          </InlineNotice>
        )}

        {resumedFrom && ep.enabled && (
          <InlineNotice
            tone="success"
            role="status"
            className={NOTICE_TIGHT}
            title="Deliveries are back on."
            action={
              <>
                <Link to={endpointDeliveriesPath(ep.id, "skipped")} className={noticeLink.green}>
                  View skipped events <ArrowRight className="w-3.5 h-3.5" />
                </Link>
                <button
                  type="button"
                  onClick={() => setResumedFrom(null)}
                  className={`inline-flex items-center py-1 text-xs text-gray-400 hover:text-white rounded-sm ${focusRing}`}
                >
                  Dismiss
                </button>
              </>
            }
          >
            New events go out as they happen. Events skipped while the endpoint was{" "}
            {resumedFrom === "disabled" ? "disabled" : "paused"} stay in the delivery history — open one to
            resend it.
          </InlineNotice>
        )}

        {idle && ep.enabled && (
          <InlineNotice>
            No live deliveries yet. Events arrive here as soon as a checkout session completes, expires or
            fails — send a test event to check your handler and signature verification now.
          </InlineNotice>
        )}

        {overlap && (
          <p className="flex items-start gap-2 text-xs text-gray-400">
            <KeyRound className="w-3.5 h-3.5 mt-0.5 shrink-0 text-gray-500" aria-hidden="true" />
            <span>
              Previous secret{" "}
              <span className="font-mono text-gray-300">••••{ep.previousSecretLast4 || "????"}</span> stays valid
              until <When iso={ep.previousSecretExpiresAt} />.
            </span>
          </p>
        )}
      </div>

      {/* ── Footer ─────────────────────────────────────────────────────── */}
      <div className="mt-4 pt-3 border-t border-gray-800/60 flex flex-wrap items-center gap-x-4 gap-y-2">
        <button
          type="button"
          onClick={() => openDialog("test")}
          disabled={anyBusy}
          className={`${secondaryBtn} py-2 disabled:cursor-not-allowed ${focusRing}`}
        >
          <Send className="w-3.5 h-3.5" /> Send test
        </button>
        <Link
          to={endpointDeliveriesPath(ep.id)}
          className={`inline-flex items-center gap-1.5 py-2 text-xs text-gray-400 hover:text-white rounded-sm ${focusRing}`}
        >
          View deliveries <ArrowRight className="w-3.5 h-3.5" />
        </Link>
        {!expanded && (
          <Link
            to={endpointPath(ep.id)}
            aria-label={`Endpoint details for ${host}`}
            className={`inline-flex items-center gap-1 py-2 text-xs text-gray-400 hover:text-white rounded-sm sm:ml-auto ${focusRing}`}
          >
            Details <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        )}
      </div>

      {/* ── Dialogs ────────────────────────────────────────────────────── */}
      <ConfirmDialog
        open={dialog === "pause"}
        onClose={() => setDialog(null)}
        onConfirm={() => {
          setDialog(null);
          void pause();
        }}
        title="Pause this endpoint?"
        description={
          waitingCount > 0
            ? `${plural(waitingCount, "delivery is", "deliveries are")} waiting to retry and will be skipped. You can resend skipped events from the delivery history.`
            : "Deliveries that are waiting to retry will be skipped. You can resend skipped events from the delivery history."
        }
        confirmLabel="Pause endpoint"
        icon={<PauseCircle className="w-5 h-5" />}
      />

      <ConfirmDialog
        open={dialog === "delete"}
        onClose={() => setDialog(null)}
        onConfirm={remove}
        busy={deleting}
        variant="danger"
        title="Delete this endpoint?"
        description={`Deliveries to ${host} stop immediately. Its delivery history stays visible for 30 days. This can't be undone.`}
        confirmLabel={deleteCooldown.active ? deleteCooldown.text : "Delete endpoint"}
        confirmDisabled={deleteCooldown.active}
        icon={<Trash2 className="w-5 h-5" />}
      >
        {deleteError && (
          <InlineNotice tone="error" role="alert">
            {deleteError}
          </InlineNotice>
        )}
      </ConfirmDialog>

      <EndpointFormModal
        open={dialog === "edit"}
        onClose={() => setDialog(null)}
        wallet={wallet}
        email={email}
        mode="edit"
        endpoint={ep}
        alertRecipient={alertRecipient}
        onSaved={(next) => {
          onChanged(withStats(next, latest.current));
          setEnableError(null);
          toast.success("Endpoint updated.");
        }}
      />

      <RotateSecretModal
        open={dialog === "rotate"}
        onClose={() => setDialog(null)}
        wallet={wallet}
        email={email}
        endpoint={ep}
        secretWritesBlocked={secretWritesBlocked}
        onRotated={(r) => {
          onChanged(withStats(r.endpoint, latest.current));
          onSecretRevealed({
            endpointId: ep.id,
            host,
            secret: r.secret,
            previousSecretExpiresAt: r.previousSecretExpiresAt,
          });
          toast.success(
            r.replacedPreviousSecret ? "Secret rotated. The older overlapping secret was dropped." : "Signing secret rotated.",
          );
        }}
      />

      <SendTestModal
        open={dialog === "test"}
        onClose={() => setDialog(null)}
        wallet={wallet}
        email={email}
        endpoint={ep}
        onSent={onTestSent}
      />
    </article>
  );
}

// ─── 24 h numbers ──────────────────────────────────────────────────────────

type Stats = NonNullable<WebhookEndpoint["stats24h"]>;
type Tone = "default" | "green" | "amber" | "red";
const TONE_TEXT: Record<Tone, string> = {
  default: "text-white",
  green: "text-[#61CE70]",
  amber: "text-amber-300",
  red: "text-red-400",
};

// Tiles from sm up; below sm a compact label/value list (four tiles in a
// ~184 px card leave ~64 px per label, which wraps unevenly).
function StatsBlock({ stats }: { stats: Stats }) {
  const settled = stats.succeeded + stats.failed;
  const rows: { label: string; short: string; value: string; tone: Tone }[] = [
    { label: "Delivered 24h", short: "Delivered (24h)", value: formatCount(stats.succeeded), tone: stats.succeeded > 0 ? "green" : "default" },
    { label: "Failed 24h", short: "Failed (24h)", value: formatCount(stats.failed), tone: stats.failed > 0 ? "red" : "default" },
    { label: "In progress", short: "In progress", value: formatCount(stats.inProgress), tone: stats.inProgress > 0 ? "amber" : "default" },
    {
      label: "Success",
      short: "Success rate",
      value: formatPercent(stats.succeeded, settled),
      tone: settled === 0 ? "default" : stats.failed === 0 ? "green" : "amber",
    },
  ];
  return (
    <>
      <div className="hidden sm:grid mt-4 grid-cols-4 gap-2">
        {rows.map((r) => (
          <StatTile key={r.label} label={r.label} value={r.value} tone={r.tone} />
        ))}
      </div>
      <dl className="sm:hidden mt-3 rounded-xl border border-gray-800 bg-gray-900/40 px-3 py-2 divide-y divide-gray-800/70">
        {rows.map((r) => (
          <div key={r.label} className="flex items-baseline justify-between gap-3 py-1.5">
            <dt className="text-[11px] text-gray-500">{r.short}</dt>
            <dd className={`font-mono text-sm tabular-nums ${TONE_TEXT[r.tone]}`}>{r.value}</dd>
          </div>
        ))}
      </dl>
    </>
  );
}

// ─── Skeleton ──────────────────────────────────────────────────────────────

/** First-load placeholder mirroring the card layout. */
export function EndpointCardSkeleton() {
  return (
    <div className="rounded-xl bg-black/20 border border-gray-800/60 p-4 animate-pulse" aria-hidden="true">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1 space-y-2.5">
          <div className="flex items-center gap-2">
            <div className="h-5 w-20 rounded-full bg-gray-800/70 shrink-0" />
            <div className="h-4 w-40 max-w-full rounded bg-gray-800/60" />
          </div>
          <div className="h-3 w-72 max-w-full rounded bg-gray-800/40" />
          <div className="h-2.5 w-52 max-w-full rounded bg-gray-800/30" />
        </div>
        <div className="h-6 w-11 rounded-full bg-gray-800/60 shrink-0" />
      </div>
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="rounded-xl border border-gray-800 bg-gray-900/40 p-3">
            <div className="h-2.5 w-16 rounded bg-gray-800/70" />
            <div className="h-3.5 w-10 rounded bg-gray-800/50 mt-2.5" />
          </div>
        ))}
      </div>
      <div className="mt-4 pt-3 border-t border-gray-800/60 flex gap-3">
        <div className="h-8 w-24 rounded-lg bg-gray-800/50" />
        <div className="h-8 w-28 rounded-lg bg-gray-800/30" />
      </div>
    </div>
  );
}

// ─── Action menu ───────────────────────────────────────────────────────────

interface MenuItem {
  key: string;
  label: string;
  icon: LucideIcon;
  onSelect: () => void;
  danger?: boolean;
  disabled?: boolean;
  /** Shown under a disabled item (touch has no tooltip). */
  hint?: string;
}

// "⋯" menu button (menu-button pattern): Enter/Space/ArrowDown open it and
// focus the first item, arrows/Home/End move, Esc closes and returns focus,
// Tab or a click outside closes it.
function ActionMenu({ label, items, disabled }: { label: string; items: MenuItem[]; disabled?: boolean }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  const enabledItems = () =>
    Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not([disabled])') ?? []);

  useEffect(() => {
    if (!open) return;
    enabledItems()[0]?.focus();
    const onPointer = (e: MouseEvent | TouchEvent) => {
      const t = e.target as Node;
      if (menuRef.current?.contains(t) || btnRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("touchstart", onPointer);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("touchstart", onPointer);
    };
  }, [open]);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  const onMenuKey = (e: ReactKeyboardEvent) => {
    const els = enabledItems();
    if (els.length === 0) return;
    const i = els.indexOf(document.activeElement as HTMLButtonElement);
    if (e.key === "ArrowDown") {
      e.preventDefault();
      els[(i + 1) % els.length].focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      els[(i - 1 + els.length) % els.length].focus();
    } else if (e.key === "Home") {
      e.preventDefault();
      els[0].focus();
    } else if (e.key === "End") {
      e.preventDefault();
      els[els.length - 1].focus();
    } else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
      btnRef.current?.focus();
    } else if (e.key === "Tab") {
      setOpen(false);
    }
  };

  const choose = (item: MenuItem) => {
    setOpen(false);
    // Focus the trigger first: a dialog opened next remembers it and gives
    // focus back to it on close (the menu item itself is about to unmount).
    btnRef.current?.focus();
    item.onSelect();
  };

  return (
    <div className="relative">
      <button
        ref={btnRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={label}
        title="More actions"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            setOpen(true);
          }
        }}
        className={`${iconBtn} ${focusRing} disabled:opacity-40 disabled:cursor-not-allowed`}
      >
        <MoreHorizontal className="w-4 h-4" />
      </button>
      {open && (
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label={label}
          onKeyDown={onMenuKey}
          className="absolute right-0 top-full mt-1 z-30 w-56 rounded-xl border border-gray-800 bg-[#0F1419] shadow-2xl shadow-black/60 p-1"
        >
          {items.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.key}
                type="button"
                role="menuitem"
                tabIndex={-1}
                disabled={item.disabled}
                onClick={() => choose(item)}
                className={`w-full flex items-start gap-2.5 px-3 py-2.5 rounded-lg text-left text-sm transition-colors focus:outline-none disabled:cursor-not-allowed ${
                  item.disabled
                    ? "text-gray-600"
                    : item.danger
                      ? "text-red-300 hover:bg-red-500/10 focus:bg-red-500/10"
                      : "text-gray-200 hover:bg-white/5 focus:bg-white/5"
                }`}
              >
                <Icon className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
                <span className="min-w-0">
                  <span className="block">{item.label}</span>
                  {item.disabled && item.hint && (
                    <span className="block text-[11px] text-gray-600 mt-0.5">{item.hint}</span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** The card's compact date format ("09:14" today, "Sep 15, 09:14" otherwise), for sibling pages. */
export function formatShortWhen(iso: string | null | undefined, dateOnly = false): string {
  const d = validDate(iso);
  return d ? shortWhen(d, dateOnly) : "—";
}
